import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalCommand,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import {
  $applyNodeReplacement,
  $getNodeByKey,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  DecoratorNode,
  createCommand,
} from 'lexical';
import { useEffect, useRef, useState, type JSX } from 'react';
import { useGT } from 'gt-react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';

/**
 * Inline image node for the rich-text editor. Round-trips to/from
 * `<img src data-document-id width>` so signature images (and their chosen
 * size) survive HTML import/export. The `data-document-id` is the durable
 * identity of the image — the backend regenerates `src` (a signed URL for
 * preview, a `cid:` ref at send time) but preserves the `width` attribute, and
 * an explicit `width` attribute renders far more reliably than CSS in Outlook.
 */

export interface ImagePayload {
  src: string;
  documentId: string;
  altText?: string;
  width?: number;
}

export type SerializedImageNode = Spread<
  {
    src: string;
    documentId: string;
    altText: string;
    width?: number;
  },
  SerializedLexicalNode
>;

export const INSERT_IMAGE_COMMAND: LexicalCommand<ImagePayload> = createCommand(
  'INSERT_IMAGE_COMMAND',
);

// Preset widths (px) offered in the size control. The numeric field allows any
// value in between; clearing it removes the explicit width and falls back to
// the responsive default cap.
const WIDTH_PRESETS: { label: string; width: number }[] = [
  { label: 'S', width: 120 },
  { label: 'M', width: 300 },
  { label: 'L', width: 500 },
];
const DEFAULT_MAX_WIDTH = 240;
const MIN_WIDTH = 40;
const MAX_WIDTH = 600;

function parseWidthAttr(value: string | null): number | undefined {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function $convertImageElement(
  domNode: HTMLElement,
): DOMConversionOutput | null {
  if (!(domNode instanceof HTMLImageElement)) return null;
  const documentId = domNode.getAttribute('data-document-id');
  if (!documentId) return null;
  return {
    node: $createImageNode({
      src: domNode.getAttribute('src') ?? '',
      documentId,
      altText: domNode.getAttribute('alt') ?? '',
      width: parseWidthAttr(domNode.getAttribute('width')),
    }),
  };
}

interface SignatureImageComponentProps {
  src: string;
  altText: string;
  width: number | undefined;
  nodeKey: NodeKey;
}

function SignatureImageComponent({
  src,
  altText,
  width,
  nodeKey,
}: SignatureImageComponentProps) {
  const gt = useGT();
  const [editor] = useLexicalComposerContext();
  const [, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLSpanElement>(null);
  // Popover visibility is local state, NOT the editor's node selection:
  // focusing the px input blurs the editor and drops the node selection, so
  // gating on selection would unmount the popover before the user can type.
  const [showControls, setShowControls] = useState(false);
  const isEditable = editor.isEditable();

  useEffect(() => {
    if (!isEditable) return;
    return editor.registerCommand<MouseEvent>(
      CLICK_COMMAND,
      (event) => {
        if (event.target === imgRef.current) {
          clearSelection();
          setSelected(true);
          setShowControls(true);
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, isEditable, clearSelection, setSelected]);

  // Close the popover only on a pointer-down outside the image and its controls.
  useEffect(() => {
    if (!showControls) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowControls(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [showControls]);

  const applyWidth = (next: number | undefined) => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isImageNode(node)) node.setWidth(next);
    });
  };

  return (
    <span
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <img
        ref={imgRef}
        src={src}
        alt={altText}
        width={width}
        style={{
          width: width ? `${width}px` : undefined,
          maxWidth: width ? '100%' : DEFAULT_MAX_WIDTH,
          height: 'auto',
          cursor: isEditable ? 'pointer' : undefined,
          outline: showControls ? '2px solid #2563eb' : undefined,
          borderRadius: 2,
        }}
      />
      {isEditable && showControls && (
        <span
          contentEditable={false}
          className="absolute left-0 top-full z-10 mt-1 flex items-center gap-1 rounded-md border border-gray-200 bg-white p-1 shadow-sm"
        >
          {WIDTH_PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              // Don't let the button's mousedown blur the editor / move the
              // caret — that would also fire the outside-pointer-down close.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => applyWidth(preset.width)}
              className={`h-6 w-6 rounded text-xs ${
                width === preset.width ? 'bg-gray-200' : 'hover:bg-gray-100'
              }`}
            >
              {preset.label}
            </button>
          ))}
          <input
            type="number"
            min={MIN_WIDTH}
            max={MAX_WIDTH}
            value={width ?? ''}
            placeholder={gt('px', {
              context: 'image width unit (pixels) placeholder in size control',
            })}
            onChange={(event) => {
              const raw = event.target.value;
              applyWidth(raw === '' ? undefined : Number(raw));
            }}
            className="h-6 w-14 rounded border border-gray-200 px-1 text-xs"
          />
        </span>
      )}
    </span>
  );
}

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __documentId: string;
  __altText: string;
  __width: number | undefined;

  static getType(): string {
    return 'signature-image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__documentId,
      node.__altText,
      node.__width,
      node.__key,
    );
  }

  constructor(
    src: string,
    documentId: string,
    altText = '',
    width?: number,
    key?: NodeKey,
  ) {
    super(key);
    this.__src = src;
    this.__documentId = documentId;
    this.__altText = altText;
    this.__width = width;
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.style.display = 'inline-block';
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  isInline(): boolean {
    return true;
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    return $createImageNode({
      src: serializedNode.src,
      documentId: serializedNode.documentId,
      altText: serializedNode.altText,
      width: serializedNode.width,
    });
  }

  exportJSON(): SerializedImageNode {
    return {
      ...super.exportJSON(),
      type: 'signature-image',
      version: 1,
      src: this.__src,
      documentId: this.__documentId,
      altText: this.__altText,
      width: this.__width,
    };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (domNode: HTMLElement) => {
        if (
          !(domNode instanceof HTMLImageElement) ||
          !domNode.hasAttribute('data-document-id')
        ) {
          return null;
        }
        return { conversion: $convertImageElement, priority: 1 };
      },
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('data-document-id', this.__documentId);
    if (this.__altText) element.setAttribute('alt', this.__altText);
    if (this.__width) {
      element.setAttribute('width', String(this.__width));
      element.setAttribute('style', 'max-width: 100%; height: auto;');
    } else {
      element.setAttribute(
        'style',
        `max-width: ${DEFAULT_MAX_WIDTH}px; height: auto;`,
      );
    }
    return { element };
  }

  getDocumentId(): string {
    return this.getLatest().__documentId;
  }

  getWidth(): number | undefined {
    return this.getLatest().__width;
  }

  setWidth(width: number | undefined): void {
    const self = this.getWritable();
    self.__width =
      width && Number.isFinite(width) && width > 0 ? width : undefined;
  }

  decorate(): JSX.Element {
    return (
      <SignatureImageComponent
        src={this.__src}
        altText={this.__altText}
        width={this.__width}
        nodeKey={this.getKey()}
      />
    );
  }
}

export function $createImageNode(payload: ImagePayload): ImageNode {
  return $applyNodeReplacement(
    new ImageNode(
      payload.src,
      payload.documentId,
      payload.altText ?? '',
      payload.width,
    ),
  );
}

export function $isImageNode(
  node: LexicalNode | null | undefined,
): node is ImageNode {
  return node instanceof ImageNode;
}
